import { useEffect, useState, useCallback } from 'react';

import {
  useCookiesSettings,
  SettingsType,
} from 'app/providers/CookiesSettingsProvider';

import { ContentSt } from '../CookieDashboard.css';
import { InfoNotification } from '../../Notification/InfoNotification';
import Translation from '../../Translation/Translation';

import { MoreInfoItem } from './MoreInfoItem';
import {
  CheckboxLabelSt,
  MoreInfoSt,
  MoreInfoSwitchSt,
  SettingCheckBoxSt,
  SettingsHeaderSt,
} from './SingleCookieSetting.css';
import { useTranslate } from 'app/shared/components/Translation/hooks/useTranslate/useTranslate';
import { useTrackingPageContext } from 'app/shared/components/PageWrapper/providers/TrackingPageProvider/TrackingPageProvider';

const isCheckedValue = (type, vals) =>
  !!Object.entries(vals).find(val => val[0] === type)[1];

interface Props {
  cookieContent: {
    type: string;
    title: string;
    content: string;
    moreInfo: {
      item: string;
      bubble: string;
    }[];
  };
  onToggle: (string, boolean) => void;
}

const SingleCookieSetting = ({ cookieContent, onToggle }: Props) => {
  const { type, title, content, moreInfo } = cookieContent;
  const isEssential = type === SettingsType.essential;

  const { trackAction } = useTrackingPageContext();

  const { essential, personal, marketing } = useCookiesSettings();
  const translate = useTranslate();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isChecked, setIsChecked] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const handleToggleClick = useCallback(() => {
    setIsCollapsed(isCollapsed => !isCollapsed);

    trackAction('cookieDashboard_cookieSettingMoreLessSwitch_click', {
      description: 'user clicks on the cookie settings more/less info switch',
    });
  }, [trackAction]);

  const handleChecked = useCallback(() => {
    setIsChecked(checked => !checked);
    setShowInfo(true);

    const tid = setTimeout(() => {
      window.location.reload();
    }, 2000);

    trackAction('cookieDashboard_cookieSettingCheckbox_click', {
      value: type,
      description: 'user clicks on the cookie settings checkbox',
    });

    onToggle(type, !isChecked);

    return () => {
      clearTimeout(tid);
    };
  }, [type, isChecked, trackAction]);

  useEffect(() => {
    const checked = isCheckedValue(type, { essential, personal, marketing });
    setIsChecked(checked);
  }, [type, essential, personal, marketing]);

  const handleOnClose = useCallback(() => {
    setShowInfo(false);
  }, []);

  return (
    <div>
      {
        <InfoNotification
          type="success"
          title={
            <Translation id="containers.cookieDashboard.singleCookieSetting.changesSaved" />
          }
          onClose={handleOnClose}
          showInfo={showInfo}
        />
      }

      <SettingsHeaderSt disabled={isEssential}>
        <SettingCheckBoxSt
          name={type}
          qaID={`cookie-setting-${type}`}
          disabled={isEssential}
          checked={isChecked}
          onChange={handleChecked}
          label={<CheckboxLabelSt>{translate({ id: title })}</CheckboxLabelSt>}
        />
      </SettingsHeaderSt>

      <ContentSt>
        <p>{translate({ id: content })}</p>

        <MoreInfoSt isCollapsed={isCollapsed}>
          {moreInfo.map((infoItem, index) => (
            <MoreInfoItem key={index} infoItem={infoItem} />
          ))}
        </MoreInfoSt>

        <MoreInfoSwitchSt onClick={handleToggleClick}>
          {isCollapsed ? (
            <Translation id="containers.cookieDashboard.singleCookieSetting.moreInfo" />
          ) : (
            <Translation id="containers.cookieDashboard.singleCookieSetting.lessInfo" />
          )}
        </MoreInfoSwitchSt>
      </ContentSt>
    </div>
  );
};

export { SingleCookieSetting };
